/**
 * Dock type definitions
 * Defines the structure for dock items and state
 */

/** Item type discriminator - system items cannot be removed */
export type DockItemType = 'system' | 'custom';

/** Action types that dock items can trigger */
export type DockActionType = `modal:${string}` | `route:${string}` | `external:${string}`;

/**
 * Represents a single item in the dock
 */
export interface DockItem {
  /** Unique identifier for the item */
  id: string;
  /** Type determines if item can be removed */
  type: DockItemType;
  /** Icon to display (emoji for now, will be replaced with icon library) */
  icon: string;
  /** Tooltip text and drawer label */
  label: string;
  /** Action to trigger when clicked */
  action: DockActionType;
  /** Order position in the dock/drawer */
  position: number;
  /** Whether item is in main dock (true) or drawer only (false) */
  isVisible: boolean;
}

/**
 * Dock dimensions for panel coordination
 */
export interface DockDimensions {
  width: number;
  height: number;
}

/**
 * Dock state managed by context
 */
export interface DockState {
  /** All dock items (visible and hidden) */
  items: DockItem[];
  /** Whether the drawer is currently open */
  isDrawerOpen: boolean;
  /** Whether edit mode is active (enables drag-and-drop) */
  isEditing: boolean;
  /** Whether the dock is currently hovered (for panel animation) */
  isDockHovered: boolean;
  /** Current dock dimensions for panel path calculations */
  dockDimensions: DockDimensions;
}

/**
 * Actions available through dock context
 */
export interface DockActions {
  /** Add a new custom item to the dock */
  addItem: (item: Omit<DockItem, 'position'>) => void;
  /** Remove an item by ID (only works for custom items) */
  removeItem: (itemId: string) => void;
  /** Reorder items by providing new order array */
  reorderItems: (itemIds: string[]) => void;
  /** Move a specific item to a new position */
  moveItem: (itemId: string, newPosition: number) => void;
  /** Toggle an item's visibility in the main dock */
  toggleItemVisibility: (itemId: string) => void;
  /** Open the drawer */
  openDrawer: () => void;
  /** Close the drawer */
  closeDrawer: () => void;
  /** Toggle the drawer open/closed state */
  toggleDrawer: () => void;
  /** Enable edit mode */
  enableEditMode: () => void;
  /** Disable edit mode */
  disableEditMode: () => void;
  /** Toggle edit mode */
  toggleEditMode: () => void;
  /** Set dock hover state (for panel animation coordination) */
  setDockHover: (isHovered: boolean) => void;
  /** Set dock dimensions (for panel path calculations) */
  setDockDimensions: (width: number, height: number) => void;
  /** Trigger an item's action */
  triggerAction: (action: DockActionType) => void;
}

/**
 * Combined context value type
 */
export interface DockContextValue extends DockState, DockActions {}

/**
 * Maximum number of visible dock items (excluding expand button)
 */
export const MAX_VISIBLE_ITEMS = 5;

/**
 * Default system items that come with the dock
 */
export const DEFAULT_DOCK_ITEMS: DockItem[] = [
  {
    id: 'search',
    type: 'system',
    icon: '🔍',
    label: 'Search Drivers',
    action: 'modal:search',
    position: 0,
    isVisible: true,
  },
  {
    id: 'tasks',
    type: 'system',
    icon: '📋',
    label: 'Tasks',
    action: 'modal:tasks',
    position: 1,
    isVisible: true,
  },
  {
    id: 'recent',
    type: 'system',
    icon: '🕐',
    label: 'Recent',
    action: 'modal:recent',
    position: 2,
    isVisible: true,
  },
  {
    id: 'settings',
    type: 'system',
    icon: '⚙️',
    label: 'Settings',
    action: 'modal:settings',
    position: 3,
    isVisible: true,
  },
];
